import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useMessage } from "../blocks/message/MessageContext";
import Ows from "../utils/OwsHandler";
import { MODULAR_HTML } from "../utils/Constants";
import { RootState } from "../store";

const ENTITY_PATH_MAP: Record<string, string> = {
  candidate: "candidates",
  relation: "crm/customers",
  relationcontact: "crm/contacts",
};

interface UseEntityExternalUrlResult {
  url: string | null;
  loading: boolean;
  error: boolean;
}

export function useEntityExternalUrl(entity: string, entityId?: string | number): UseEntityExternalUrlResult {
  const { showMessage } = useMessage();
  const defaultLanguage = useSelector((state: RootState) => state.user?.defaultContentLanguage) || "en";

  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (!entity || !entityId) {
      setUrl(null);
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const { response } = await Ows.handleRequest("Otys.Services.SettingsService.get", [
          { phoenix_fo: ["domain_for_client", "phoenix_domain_prefix"] },
          null,
        ]);

        const domain = response.phoenix_fo.domain_for_client || process.env.OWS_DEFAULT_DOMAIN;
        const domainPrefix = response.phoenix_fo.phoenix_domain_prefix || process.env.OWS_DEFAULT_DOMAIN_PREFIX;
        const path = ENTITY_PATH_MAP[entity.toLowerCase()];

        if (path && entityId) {
          const fullUrl = `https://${domainPrefix}.${domain}.com/${defaultLanguage}/${MODULAR_HTML}/${path}/${entityId}`;
          setUrl(fullUrl);
          setError(false);
        } else {
          setUrl(null);
          setError(true);
        }
      } catch (err) {
        console.error("Error fetching external URL:", err);
        setUrl(null);
        setError(true);
        showMessage("error", "Failed to generate external entity URL");
      } finally {
        setLoading(false);
      }
    })();
  }, [entity, entityId]);

  return { url, loading, error };
}